import { Media } from 'lib-shared-types-channels';
import { memo, useMemo } from 'react';

import { dashboardApi } from 'apis/dashboard';
import { DashboardCard } from 'app/views/layout/views/dashboard/components/card';

import {
  ChannelDashboardChannelContext,
  ChannelDashboardChannelContextValue,
} from './channelDashboardChannel.context';
import { ChannelDashboardChannelActions } from './components/actions/channelDashboardChannelActions';
import { ChannelDashboardMetrics } from './components/metrics/channelDashboardMetrics';
import { ChannelDashboardChannelTitle } from './components/title/channelDashboardChannelTitle';
import { dashboardMediaToChannels } from './utils/dashboardMediaToChannel';

type ChannelDashboardChannelProps = {
  /** The channel media type. */
  media: Media;
};

export const ChannelDashboardChannel = memo(function ChannelDashboardChannel({
  media,
}: ChannelDashboardChannelProps): JSX.Element {
  const channels = useMemo(() => dashboardMediaToChannels[media], [media]);

  const { channelMetrics, isSkeleton } =
    dashboardApi.endpoints.readChannelDashboardMetrics.useQueryState(
      undefined,
      {
        selectFromResult: ({ data, isUninitialized, isFetching }) => ({
          channelMetrics: data?.data.channel_metrics ?? [],
          isSkeleton: isUninitialized || isFetching,
        }),
      },
    );

  const contextValue: ChannelDashboardChannelContextValue = useMemo(() => {
    const { metrics, sla_policies } =
      channelMetrics.find(({ id }) => id === media) ?? {};

    return {
      media,
      channels,
      metrics,
      slaPolicies: sla_policies,
      isSkeleton,
    };
  }, [channelMetrics, channels, isSkeleton, media]);

  return (
    <ChannelDashboardChannelContext.Provider value={contextValue}>
      <DashboardCard
        key={media}
        titleSlot={<ChannelDashboardChannelTitle />}
        actionsSlot={<ChannelDashboardChannelActions />}
      >
        <ChannelDashboardMetrics />
      </DashboardCard>
    </ChannelDashboardChannelContext.Provider>
  );
});
